import { get, orderBy } from 'lodash';
import * as moment from 'moment';
import { mstohm } from 'utils/time';

/**
 * Adds _start and _end property with value of type Moment.js Date to every item in history
 *
 * @param {Array<Object>} history
 * @returns {Array<Object>} history with _start and _end properties
 */
export function addMomentJSDates(history) {
  history = orderBy(history, 'start_ts', 'asc');
  history = history.map(item => {
    const start = item.start;
    const end = item.end;
    const _start = moment.parseZone(start);
    let _end = moment.parseZone(end);

    // If it is the current event, end will be null. parseZone(null) will give Invalid date
    if (!_end.isValid()) {
      // Get the utc offset of start time and change the offset of the current time to the start offset
      _end = moment().utcOffset(_start.utcOffset());
    }

    return { ...item, _start, _end };
  });

  return history;
}

/**
 * Remove start and end time overflow from item
 *
 * @param {Object} item
 * @param {Object} bounds
 * @returns item without overflow
 */
export function removeItemOverflow(item, bounds) {
  const { startOfDay, endOfDay } = bounds;

  if (item._start.isBefore(startOfDay)) {
    item._start = startOfDay;
  }

  if (item._end.isAfter(endOfDay)) {
    item._end = endOfDay;
  }

  return item;
}

/**
 * Gets all moments that overlap with given item
 *
 * @export
 * @param {Object} item
 * @param {Array<Object>} moments
 * @returns moments that overlap with item
 */
export function getIncludedMoments(item, moments) {
  return moments
    .filter(m => isMomentIncluded(item, m))
    .map(m => addMomentDuration(item, m))
    .filter(m => m._duration >= 60000);
}

export function getIncludedCustomEvents(item, customEvents) {
  const utcOffset = item._start.utcOffset();

  return customEvents
    .map(e => ({
      ...e,
      _start: e._start.utcOffset(utcOffset),
      _end: e._end.utcOffset(utcOffset),
    }))
    .filter(e => {
      const { end, _start, _end } = e;

      if (end) {
        return (
          _start.isBetween(item._start, item._end, 'minutes', '[)') ||
          _end.isBetween(item._start, item._end, 'minutes', '(]') ||
          (_start.isBefore(item._start) && _end.isAfter(item._end))
        );
      }

      return _start.isBetween(item._start, item._end, 'minutes', '[)');
    });
}

/**
 * Checks if a moment overlaps with an item or not
 *
 * @param {Object} item
 * @param {Object} momentItem
 * @returns boolean indicating if moment is overlapping with item or not
 */
function isMomentIncluded(item, momentItem) {
  const { type, _start, _end } = momentItem;

  if (type !== 'GenericMoment') {
    return false;
  }

  if (
    _start.isBetween(item._start, item._end, null, '[)') ||
    _end.isBetween(item._start, item._end, null, '(]') ||
    (_start.isSameOrBefore(item._start) && _end.isSameOrAfter(item._end))
  ) {
    return true;
  }

  return false;
}

/**
 * Add duration to moment item
 *
 * @param {*} item
 * @param {*} momentItem
 * @returns item enhanced with duration information
 */
function addMomentDuration(item, momentItem) {
  const { _start, _end } = momentItem;
  const start = _start.isSameOrAfter(item._start) ? _start : item._start;
  const end = _end.isSameOrBefore(item._end) ? _end : item._end;
  const momentDuration = end - start;
  const eventDuration = item._end - item._start;
  const _duration = momentDuration;
  const duration =
    momentDuration < eventDuration ? mstohm(momentDuration) : undefined;

  return { ...momentItem, _duration, duration };
}
